/**
 * Copy and prompt data for the Arabic Radar landing page.
 *
 * Lifted verbatim from `docs/design-references/radar-arabic.html` so the Arabic
 * wording stays exactly as reviewed. Do not machine-translate or "tidy" these
 * strings — the prompt library is written in Modern Standard Arabic with
 * market-specific variants that were checked against native usage, which is
 * the whole point of the page.
 */

export type MarketKey = 'saudi' | 'uae' | 'gcc'
export type LanguageKey = 'ar' | 'en' | 'bilingual'
export type PromptKey = 'discover' | 'compare' | 'trust' | 'accuracy'
export type ModelKey = 'ChatGPT' | 'Claude' | 'Gemini' | 'Perplexity'

export const MARKET_LABELS: Record<MarketKey, string> = {
  saudi: 'السعودية',
  uae: 'الإمارات',
  gcc: 'عرض إقليمي لدول مجلس التعاون',
}

export const LANGUAGE_LABELS: Record<LanguageKey, string> = {
  ar: 'العربية',
  en: 'English',
  bilingual: 'العربية + English',
}

export const MARKET_OPTIONS: { value: MarketKey; label: string }[] = [
  { value: 'saudi', label: 'السعودية' },
  { value: 'uae', label: 'الإمارات' },
  { value: 'gcc', label: 'عرض إقليمي' },
]

export const LANGUAGE_OPTIONS: { value: LanguageKey; label: string }[] = [
  { value: 'ar', label: 'العربية' },
  { value: 'en', label: 'English' },
  { value: 'bilingual', label: 'ثنائي اللغة' },
]

export const PROMPT_TABS: { value: PromptKey; label: string }[] = [
  { value: 'discover', label: 'اكتشاف العلامة' },
  { value: 'compare', label: 'مقارنة الخيارات' },
  { value: 'trust', label: 'الثقة والسمعة' },
  { value: 'accuracy', label: 'دقة المعلومات' },
]

export const MODELS: ModelKey[] = ['ChatGPT', 'Claude', 'Gemini', 'Perplexity']

type PromptEntry = { text: string; code: string; note: string }

export const PROMPT_LIBRARY: Record<
  PromptKey,
  Record<MarketKey, PromptEntry>
> = {
  discover: {
    saudi: {
      text: 'ما أفضل منصات مراقبة ظهور العلامات التجارية في إجابات الذكاء الاصطناعي للشركات السعودية؟',
      code: 'MSA · SA',
      note: 'مثال لصيغة سعودية تُستخدم فقط بعد مراجعتها مع متحدث من السوق: «وش أفضل منصة تبيّن لنا كيف تظهر علامتنا في ChatGPT داخل السعودية؟»',
    },
    uae: {
      text: 'ما أفضل المنصات لمتابعة ظهور العلامة في إجابات الذكاء الاصطناعي للشركات في الإمارات؟',
      code: 'MSA · AE',
      note: 'تظل الصياغة بالفصحى في هذا المثال؛ ولا تُضاف صيغة محلية إلا بعد مراجعتها مع متحدث من السوق.',
    },
    gcc: {
      text: 'ما أفضل أدوات مراقبة ظهور العلامات التجارية في الذكاء الاصطناعي للشركات في دول مجلس التعاون؟',
      code: 'MSA · GCC',
      note: 'عرض إقليمي يقارن السياقات المشتركة من دون افتراض أن سلوك البحث واحد في كل دولة.',
    },
  },
  compare: {
    saudi: {
      text: 'قارن بين أفضل منصات قياس ظهور العلامة في ChatGPT للشركات السعودية من حيث الدقة والأدلة.',
      code: 'COMPARE · SA',
      note: 'سؤال مقارنة يختبر ترتيب الخيارات ومعايير المفاضلة والمصادر التي يعتمد عليها النموذج.',
    },
    uae: {
      text: 'ما الفرق بين منصات مراقبة ظهور العلامة في الذكاء الاصطناعي المناسبة للشركات في الإمارات؟',
      code: 'COMPARE · AE',
      note: 'سؤال مقارنة يختبر وضوح الفئة ومدى ارتباط كل خيار بالسوق الإماراتي.',
    },
    gcc: {
      text: 'قارن أفضل حلول مراقبة ظهور العلامة في إجابات الذكاء الاصطناعي للشركات في دول مجلس التعاون.',
      code: 'COMPARE · GCC',
      note: 'مقارنة إقليمية تُظهر من يملك قصة أوضح عبر أكثر من سوق خليجي.',
    },
  },
  trust: {
    saudi: {
      text: 'ما أكثر منصات مراقبة ظهور العلامة في الذكاء الاصطناعي موثوقية للشركات في السعودية؟',
      code: 'TRUST · SA',
      note: 'يكشف السؤال إشارات الثقة التي يرجّحها النموذج: العملاء، المنهجية، التغطية، أو الأدلة المنشورة.',
    },
    uae: {
      text: 'ما المنصات الموثوقة لقياس حضور العلامة في إجابات الذكاء الاصطناعي داخل الإمارات؟',
      code: 'TRUST · AE',
      note: 'يختبر مصادر السمعة المحلية ومدى وضوح الدليل الذي تستند إليه التوصية.',
    },
    gcc: {
      text: 'ما الحلول الموثوقة لمراقبة حضور العلامات التجارية في دول مجلس التعاون داخل نماذج الذكاء الاصطناعي؟',
      code: 'TRUST · GCC',
      note: 'يقارن إشارات الثقة المشتركة والإشارات الخاصة بكل سوق خليجي.',
    },
  },
  accuracy: {
    saudi: {
      text: 'ماذا تعرف نماذج الذكاء الاصطناعي عن خدمات علامتنا في السعودية، وهل المعلومات دقيقة؟',
      code: 'ACCURACY · SA',
      note: 'يفحص الوصف والمنتج والسوق والحقائق الأساسية لرصد التضارب أو المعلومات القديمة.',
    },
    uae: {
      text: 'كيف تصف نماذج الذكاء الاصطناعي علامتنا في الإمارات، وما المعلومات غير الدقيقة؟',
      code: 'ACCURACY · AE',
      note: 'يفصل بين غياب العلامة وظهورها بوصف خاطئ أو منقول من سوق آخر.',
    },
    gcc: {
      text: 'هل تصف نماذج الذكاء الاصطناعي خدمات علامتنا بدقة ضمن سياق دول مجلس التعاون؟',
      code: 'ACCURACY · GCC',
      note: 'يكشف اختلاف وصف العلامة بين الأسواق واللغات والمصادر المؤثرة.',
    },
  },
}

/**
 * Illustrative model responses. Each carries one `<mark>` span in the source;
 * rendered as segments rather than raw HTML so nothing needs
 * `dangerouslySetInnerHTML` (banned repo-wide — CLAUDE.md §6).
 */
export const RESPONSE_LIBRARY: Record<
  ModelKey,
  { before: string; mark: string; after: string }
> = {
  ChatGPT: {
    before: 'رشّح النموذج ثلاثة خيارات عالمية، لكنه ',
    mark: 'لم يذكر علامتك',
    after:
      ' ولم يستند إلى أي مصدر عربي من موقعك. ظهر منافسان في إجابته، وتكرر الاستشهاد بصفحات مقارنة محلية أوضح.',
  },
  Claude: {
    before: 'ذكر النموذج علامتك مرة واحدة، لكنه ',
    mark: 'وصف المنتج بصورة غير مكتملة',
    after: '. اعتمد على مصدر إنجليزي قديم ولم يربط العلامة بالسوق المحلي.',
  },
  Gemini: {
    before:
      'ظهرت علامتك ضمن الخيارات، واحتل منافس المرتبة الأولى. تكرر الاستشهاد بثلاثة مصادر عربية تحمل ',
    mark: 'إشارات ثقة محلية أوضح',
    after: '؛ وهذا نمط يستحق المراجعة.',
  },
  Perplexity: {
    before: 'استشهد النموذج بمصدرين وذكر منافسين اثنين. ',
    mark: 'لم يستشهد بموقعك',
    after: ' رغم ظهوره في البحث؛ وهذا نمط يستحق مراجعة وضوح الصفحة العربية.',
  },
}

export const AUDIT_STEPS = [
  {
    signal: 'SA',
    title: 'حلّل السياق الذي يهمك',
    copy: 'نكيّف الأسئلة والكيانات والمنافسين وفق السياق المختار، ولا نعامل العرض الإقليمي كبديل عن تحليل كل دولة.',
    tag: 'MARKET SIGNAL',
  },
  {
    signal: 'AR',
    title: 'اسأل كما يسأل جمهورك',
    copy: 'اختبر فصحى طبيعية مع صيغ سعودية وخليجية عندما يغيّر السياق المحلي طريقة السؤال.',
    tag: 'NATIVE PROMPTS',
  },
  {
    signal: '↗',
    title: 'شاهد الدليل وراء كل نتيجة',
    copy: 'راجع الإجابة والاستشهادات والإشارات والمنافسين المذكورين. لا تعتمد على درجة مجرّدة فقط.',
    tag: 'CITED EVIDENCE',
  },
  {
    signal: 'PDF',
    title: 'حوّل النتيجة إلى قرار',
    copy: 'رتّب فرص المحتوى حسب الأولوية وشارك تقريرًا ثنائي اللغة مع فريقك أو عميلك.',
    tag: 'ACTION REPORT',
  },
]

export const CAPABILITIES = [
  {
    value: '3',
    label: 'سياقات تحليل: السعودية، الإمارات، وعرض إقليمي لدول مجلس التعاون',
  },
  { value: 'AR + EN', label: 'العربية والإنجليزية في تدقيق واحد' },
  { value: 'LLMs', label: 'نؤكد النماذج المشمولة بحسب توافرها وقت التنفيذ' },
  { value: '1', label: 'تقرير ثنائي اللغة يسلّمه الفريق' },
]

/** Also mirrored into FAQPage JSON-LD in page.tsx — keep the two in step. */
export const FAQ_ITEMS = [
  {
    q: 'ما المقصود بظهور العلامة في الذكاء الاصطناعي؟',
    a: 'هو مدى تكرار ظهور علامتك، وطريقة وصفها، وموقعها بين المنافسين في إجابات نماذج الذكاء الاصطناعي.',
  },
  {
    q: 'هل يدعم Radar العربية والإنجليزية؟',
    a: 'نعم. يشمل البرنامج التجريبي تدقيقًا بالعربية أو الإنجليزية، أو مقارنة اللغتين ضمن تقرير واحد.',
  },
  {
    q: 'ماذا يعني اختيار سوق التحليل؟',
    a: 'يمكنك اختيار السعودية أو الإمارات أو عرض إقليمي لدول مجلس التعاون. يضبط الاختيار الأسئلة والكيانات والمنافسين والنية المحلية، ولا يعني أن النموذج يعمل من موقع جغرافي مختلف.',
  },
  {
    q: 'هل الأسئلة العربية مترجمة من الإنجليزية؟',
    a: 'لا. يراجع الفريق أسئلة عربية فصيحة، ثم يضيف صيغًا سعودية أو خليجية عندما تكون ملائمة فعلًا لسياق السوق.',
  },
  {
    q: 'ما النماذج التي يفحصها Radar؟',
    a: 'يشمل نطاق البرنامج التجريبي إجابات ChatGPT و Claude و Gemini و Perplexity. تمثل النتيجة لقطة وقت التدقيق، وقد تختلف تطبيقات المستهلك بسبب التخصيص أو البحث المباشر أو تحديثات النماذج.',
  },
  {
    q: 'هل أستطيع رؤية الدليل وراء كل نتيجة؟',
    a: 'نعم. يتضمن التقرير السؤال وإجابة النموذج والإشارات والاستشهادات والمنافسين الذين ظهروا في الإجابة، مع الحفاظ على الاتجاه الصحيح للمحتوى المختلط.',
  },
  {
    q: 'هل يمكن مشاركة التقرير مع العميل أو الإدارة؟',
    a: 'نعم. ضمن البرنامج التجريبي يسلّمك الفريق تقرير PDF ثنائي اللغة يمكنك مشاركته مع العميل أو الإدارة.',
  },
]
